// PostHog product analytics wrapper (M-C).
//
// This is the SINGLE entry point the rest of the app talks to when it
// wants to record a user-meaningful event. Call sites never import
// posthog-js directly; that way:
//   • we can swap providers (Amplitude / Mixpanel / nothing) without
//     touching call sites,
//   • the wrapper no-ops cleanly when VITE_POSTHOG_KEY is unset, so
//     local dev doesn't dial out and CI doesn't need secrets,
//   • the event taxonomy lives in one file (this one), making it easy
//     to audit "what does Tradeatory know about its users?"
//
// Scope for M-C: the core funnel events PLUS the failure/drop-off events
// below. Adding new events is intentional friction — every new entry
// needs a code change here AND review of what we're tracking.
//
// Invite-batch observability: you learn more from where users BAIL than
// where they succeed. The *_failed / *_no_trades / *_abandoned events
// make every funnel-stage drop-off visible instead of a silent gap.

import posthog from 'posthog-js';
import { logger } from './logger';

// Exhaustive list of event names. TypeScript will error on any
// `track('something_else', ...)` call site that drifts off this list.
export type EventName =
  | 'signup_completed'
  | 'backtest_run_started'
  | 'backtest_run_completed'
  | 'variant_saved'
  | 'ai_optimize_started'
  | 'ai_optimize_completed'
  | 'portfolio_evaluated'
  | 'quota_exceeded'
  // ── Failure / drop-off (invite-batch observability) ──────────────────
  | 'backtest_run_failed'    // engine/validation threw before a result
  | 'backtest_no_trades'     // ran clean but produced zero trades
  | 'ai_optimize_failed'     // optimize aborted or errored (non-quota)
  | 'data_load_failed'       // OHLCV / market-data load threw
  | 'onboarding_abandoned'   // left the first-run flow before finishing
  // ── P15 — Strategy Builder from-scratch generation (TSS observability) ─
  | 'builder_generation_completed' // json | tss_manual | tss_retry_after_json_timeout
  | 'builder_generation_failed'
  // ── Iter 12C — Pine→TSL importer gated-beta (privacy-safe: NEVER logs source) ─
  | 'pine_import_attempted'  // user ran the converter
  | 'pine_import_converted'  // produced a result (any confidence)
  | 'pine_import_failed'     // converter threw / failed closed
  | 'pine_import_accepted'   // user took the import forward (backtest/save)
  | 'pine_import_aborted'    // user backed out after seeing the report
  // ── Template gallery + AI-catalog observability ──────────────────────
  | 'template_gallery_opened'  // opened the curated-template gallery
  | 'template_loaded'          // loaded a curated template into the builder
  | 'template_load_cancelled'  // backed out before loading a template
  | 'ai_catalog_refs_dropped'  // AI emitted library refs that were dropped server-side
  // ── Discovery Engine Phase 3B pilot (privacy-safe: ONLY categorical/count
  //    metadata — NEVER strategy payloads, blocks, code, raw hypothesis text,
  //    credentials, or financial evidence; enforced by discoveryTelemetry) ──────
  | 'discovery_entry_attempted'     // opened/selected a hypothesis to explore
  | 'discovery_request_admitted'    // request passed admission (family resolved)
  | 'discovery_request_rejected'    // request rejected at admission (+reason code)
  | 'discovery_run_started'         // a bounded run began
  | 'discovery_run_completed'       // run completed (+counts, +status)
  | 'discovery_run_cancelled'       // budget exhausted / cancelled (partial evidence kept)
  | 'discovery_no_candidate'        // scientifically-valid no-promotion outcome
  | 'discovery_candidate_reviewed'  // candidate evidence opened
  | 'discovery_promotion_attempted' // user clicked Send to Builder
  | 'discovery_promotion_accepted'  // canonical promotion gate + compile succeeded
  | 'discovery_promotion_rejected'  // promotion blocked / compile failed closed
  | 'discovery_builder_handoff'     // declarative handoff to the Builder (+ok)
  | 'discovery_persist_saved'       // durable finalize confirmed (+ok, +backendMode)
  | 'discovery_persist_reloaded'    // run restored from the repository (+ok)
  | 'discovery_validation_linked';  // provenance reached the Builder/Validation linkage

let enabled = false;

export function initAnalytics(): void {
  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  if (!key) {
    logger.debug('[analytics] VITE_POSTHOG_KEY not set — analytics disabled.');
    return;
  }
  posthog.init(key, {
    api_host: (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'https://us.posthog.com',
    // Explicit zero autocapture / pageview / pageleave. We send only the
    // events listed in EventName, nothing else — no DOM scraping, no
    // implicit URL tracking. Audit-friendly + privacy-protective.
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    // Defer until we explicitly identify() — avoids burning anonymous
    // events on the auth gate before the user signs in.
    person_profiles: 'identified_only',
    // Quiet in dev console.
    loaded: () => { logger.debug('[analytics] PostHog loaded.'); },
  });
  enabled = true;
}

// Tie subsequent events to a signed-in user. Properties become "person
// properties" on PostHog (tier, etc.). Called from AuthContext when the
// auth state resolves.
export function identify(userId: string, props?: Record<string, unknown>): void {
  if (!enabled) return;
  posthog.identify(userId, props);
}

// Clear identity on sign-out so a shared device doesn't conflate users.
export function resetIdentity(): void {
  if (!enabled) return;
  posthog.reset();
}

// Fire a typed event. No-op when analytics isn't initialised. Errors
// inside the SDK are swallowed — telemetry must never break the app.
export function track(event: EventName, props?: Record<string, unknown>): void {
  if (!enabled) return;
  try {
    posthog.capture(event, props);
  } catch (err) {
    logger.warn('[analytics] capture failed:', err);
  }
}
